import { useEffect, useRef, useState } from 'react';

/**
 * useAdaptiveTypewriter — Affiche progressivement un texte qui peut changer (streaming SSE).
 *
 * Si les chunks SSE arrivent groupés (batching Nginx, thinking Sonnet), le texte cible
 * saute de 0 à N caractères d'un coup. Ce hook lisse l'affichage : il rattrape
 * adaptativement — plus l'écart entre ce qui est affiché et la cible est grand,
 * plus il avance vite. En fin de stream (target stable), il termine à vitesse normale.
 *
 * - target: string courant (peut croître au fil du stream SSE)
 * - options.tickMs: intervalle d'animation (défaut 16ms = ~60fps)
 * - options.minStep: chars min par tick (défaut 1)
 * - options.maxStep: chars max par tick (défaut 40)
 *
 * Retourne le texte à afficher progressivement.
 */
export function useAdaptiveTypewriter(target, options = {}) {
  const { tickMs = 16, minStep = 1, maxStep = 40 } = options;
  const [displayed, setDisplayed] = useState('');
  const targetRef = useRef(target || '');

  // Synchroniser la cible dans la ref (lue par l'intervalle sans re-render)
  useEffect(() => {
    targetRef.current = target || '';
    // Reset explicite si on passe à vide ou si le target recule drastiquement
    // (nouveau message streaming, clear_stream, changement de session)
    if (!target) {
      setDisplayed('');
      return;
    }
    setDisplayed(prev => {
      if (prev.length > target.length) return target; // clear ou recul → rattrapage
      return prev;
    });
  }, [target]);

  useEffect(() => {
    const id = setInterval(() => {
      setDisplayed(prev => {
        const t = targetRef.current;
        if (prev.length >= t.length) return prev;
        const gap = t.length - prev.length;
        const step = Math.min(maxStep, Math.max(minStep, Math.floor(gap / 20) + minStep));
        return t.slice(0, prev.length + step);
      });
    }, tickMs);
    return () => clearInterval(id);
  }, [tickMs, minStep, maxStep]);

  return displayed;
}
